import { useEffect, useState, lazy, Suspense } from "react";
import "@/App.css";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Invest from "@/pages/Invest";
import BackUsPage from "@/pages/BackUsPage";
import JoinUs from "@/pages/JoinUs";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import LegalPage from "@/pages/LegalPage";
import ShareButtons from "@/components/ShareButtons";
import SEO from "@/components/SEO";
import ThemePicker from "@/components/ThemePicker";
import CookieConsent from "@/components/CookieConsent";
import MobileBottomNav from "@/components/MobileBottomNav";
import BackToTop from "@/components/BackToTop";
import ExitIntentModal from "@/components/ExitIntentModal";
import { LanguageProvider } from "@/i18n";
import { applyTheme, getStoredTheme } from "@/theme";

const Assessment = lazy(() => import("@/pages/assessment/Assessment"));
const AssessmentResult = lazy(() => import("@/pages/assessment/AssessmentResult"));
const Level1 = lazy(() => import("@/pages/assessment/Level1"));
const Level2 = lazy(() => import("@/pages/assessment/Level2"));

const ScrollTop = () => {
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
    try {
      if (!sessionStorage.getItem("tlp-landing")) {
        sessionStorage.setItem("tlp-landing", pathname + search);
      }
    } catch { /* private mode */ }
  }, [pathname, search]);
  return null;
};

function App() {
  const [theme, setTheme] = useState(() => getStoredTheme());

  useEffect(() => {
    if (theme) applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    const onAnchor = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const target = document.querySelector(a.getAttribute("href"));
      if (target) {
        e.preventDefault();
        lenis.scrollTo(target, { offset: -72 });
      }
    };
    document.addEventListener("click", onAnchor);

    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
      document.removeEventListener("click", onAnchor);
    };
  }, []);

  return (
    <div className="App bg-void text-white">
      <div className="grain-overlay" aria-hidden="true" />
      <BrowserRouter>
        <LanguageProvider>
          <ScrollTop />
          <SEO />
          <Header
            theme={theme || "dark"}
            onToggleTheme={() => setTheme((t) => (t === "light" ? "dark" : "light"))}
          />
          <ShareButtons />
          <div className="pb-[60px] lg:pb-0">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/back-us" element={<Invest />} />
          <Route path="/subscribe" element={<BackUsPage />} />
          <Route path="/apply" element={<JoinUs />} />
          {/* legacy URL redirects after the 2026 rename */}
          <Route path="/invest" element={<Navigate to="/back-us" replace />} />
          <Route path="/join-us" element={<Navigate to="/apply" replace />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/terms" element={<LegalPage page="terms" />} />
          <Route path="/privacy" element={<LegalPage page="privacy" />} />
          <Route path="/refund" element={<LegalPage page="refund" />} />
          <Route path="/faq" element={<LegalPage page="faq" />} />
          <Route path="/assessment" element={<Suspense fallback={<div className="min-h-screen" />}><Assessment /></Suspense>} />
          <Route path="/assessment/result/:token" element={<Suspense fallback={<div className="min-h-screen" />}><AssessmentResult /></Suspense>} />
          <Route path="/assessment/level-1" element={<Suspense fallback={<div className="min-h-screen" />}><Level1 /></Suspense>} />
          <Route path="/assessment/level-1/results/:token" element={<Suspense fallback={<div className="min-h-screen" />}><Level1 /></Suspense>} />
          <Route path="/assessment/level-2" element={<Suspense fallback={<div className="min-h-screen" />}><Level2 /></Suspense>} />
          <Route path="/assessment/level-2/results/:token" element={<Suspense fallback={<div className="min-h-screen" />}><Level2 /></Suspense>} />
        </Routes>
          <Footer />
          </div>
          <MobileBottomNav />
          <BackToTop />
          <ExitIntentModal />
          {!theme && <ThemePicker onChoose={setTheme} />}
          {theme && <CookieConsent />}
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
